import { ConfirmationAction } from '@app/components/ConfirmationAction';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  WebAppCompositionSocialUploadItemDto,
  WebAppFragmentSocialUploadItemDto,
} from '@mi-front/api-v2';
import BuildIcon from '@mui/icons-material/Build';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { UploadCardDeleteDialog } from './UploadCardDeleteDialog';
import { publishCardMessages } from '../publish-messages';

type UploadCardDto = WebAppCompositionSocialUploadItemDto | WebAppFragmentSocialUploadItemDto;

export interface UploadCardActionsMenuProps {
  upload: UploadCardDto;
  platform: SocialPlatform;
  anchorEl: HTMLElement | null;
  open: boolean;
  deleteDialogOpen: boolean;
  isDeleting: boolean;
  isPromoting: boolean;
  onClose: () => void;
  onDeleteDialogOpen: () => void;
  onDeleteDialogClose: () => void;
  onDelete: () => Promise<void>;
  onPromote: () => Promise<void>;
  onRetry: () => void;
}

/**
 * Overflow menu and delete dialog for a social upload card (completed / scheduled / failed actions).
 */
export function UploadCardActionsMenu({
  upload,
  platform,
  anchorEl,
  open,
  deleteDialogOpen,
  isDeleting,
  isPromoting,
  onClose,
  onDeleteDialogOpen,
  onDeleteDialogClose,
  onDelete,
  onPromote,
  onRetry,
}: UploadCardActionsMenuProps) {
  const { $t } = useIntl();

  const status = upload.status;
  const platformFeatures = upload.platformFeatures;
  const isCompleted = status === 'COMPLETED';
  const isScheduled = status === 'SCHEDULED';
  const isFailed = status === 'FAILED';
  const isWaitingPublicLink = isCompleted && !platformFeatures?.publicUrl;

  const hasManageUrl =
    platformFeatures &&
    platformFeatures.manageUrl &&
    platformFeatures.manageUrl !== platformFeatures.publicUrl;

  const hasCompletedMenuItems =
    isCompleted && ((hasManageUrl && platformFeatures?.manageUrl) || platformFeatures?.isDeletable);
  const hasAnyMenuItems = hasCompletedMenuItems || isScheduled || isFailed;

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open && !isWaitingPublicLink}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!hasAnyMenuItems ? (
          <MenuItem disabled sx={{ cursor: 'default' }}>
            <ListItemText>
              <FormattedMessage {...publishCardMessages.noAdditionalActions} />
            </ListItemText>
          </MenuItem>
        ) : (
          <>
            {isCompleted && hasManageUrl && platformFeatures?.manageUrl && (
              <MenuItem
                component="a"
                href={platformFeatures.manageUrl}
                target="_blank"
                rel="noreferrer"
                onClick={onClose}
              >
                <ListItemIcon>
                  <BuildIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage
                    {...publishCardMessages.manageVideo}
                    values={{ platform: platform.name }}
                  />
                </ListItemText>
              </MenuItem>
            )}
            {isCompleted && platformFeatures?.isDeletable && (
              <MenuItem
                onClick={() => {
                  onClose();
                  onDeleteDialogOpen();
                }}
                disabled={isDeleting}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage
                    {...publishCardMessages.deleteFromPlatform}
                    values={{ platform: platform.name }}
                  />
                </ListItemText>
              </MenuItem>
            )}
            {isScheduled && (
              <>
                <ConfirmationAction
                  title={$t(publishCardMessages.publishNowConfirmTitle)}
                  message={<FormattedMessage {...publishCardMessages.publishNowConfirmMessage} />}
                  confirmLabel={$t(publishCardMessages.publishNow)}
                  cancelLabel={$t({
                    id: 'common.cancel',
                    defaultMessage: 'Cancelar',
                  })}
                  confirmColor="primary"
                  onConfirm={onPromote}
                >
                  {({ onClick }) => (
                    <MenuItem
                      onClick={() => {
                        onClose();
                        onClick();
                      }}
                      disabled={isPromoting}
                    >
                      <ListItemIcon>
                        <SendIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        <FormattedMessage {...publishCardMessages.publishNow} />
                      </ListItemText>
                    </MenuItem>
                  )}
                </ConfirmationAction>
                <ConfirmationAction
                  title={$t(publishCardMessages.deleteScheduledPublishTitle)}
                  message={
                    <FormattedMessage {...publishCardMessages.deleteScheduledPublishMessage} />
                  }
                  confirmLabel={$t({
                    id: 'common.delete',
                    defaultMessage: 'Eliminar',
                  })}
                  cancelLabel={$t({
                    id: 'common.cancel',
                    defaultMessage: 'Cancelar',
                  })}
                  confirmColor="error"
                  onConfirm={onDelete}
                >
                  {({ onClick }) => (
                    <MenuItem
                      onClick={() => {
                        onClose();
                        onClick();
                      }}
                      disabled={isDeleting}
                    >
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>
                        <FormattedMessage id="common.delete" defaultMessage="Eliminar" />
                      </ListItemText>
                    </MenuItem>
                  )}
                </ConfirmationAction>
              </>
            )}
            {isFailed && (
              <MenuItem onClick={onRetry}>
                <ListItemIcon>
                  <SendIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  <FormattedMessage {...publishCardMessages.retry} />
                </ListItemText>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
      <UploadCardDeleteDialog
        open={deleteDialogOpen}
        platformName={platform.name}
        isDeleting={isDeleting}
        onClose={onDeleteDialogClose}
        onConfirm={onDelete}
      />
    </>
  );
}
